import { formatPhoneNumber, TelecomPhoneLineId, WorkflowActionId } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import type { inferRouterOutputs } from "@trpc/server";
import { TopAlignedCheckbox } from "components/ui/checkbox";
import { Select } from "components/ui/select";
import { produce } from "immer";
import React from "react";
import { smsActionBlocker } from "./builder/builder-helper";
import { EditableWorkflow } from "./types";

export type WorkflowPhoneLine =
    inferRouterOutputs<AppRouter>["telecom"]["listWorkflowPhoneLines"]["phoneLines"][number];

interface SmsActionEditorProps {
    workflow: EditableWorkflow;
    actionId: WorkflowActionId;
    onChange: (workflow: EditableWorkflow) => void;
    phoneLines: WorkflowPhoneLine[];
    phoneLinesLoaded: boolean;
}

type EditableWorkflowAction = EditableWorkflow["actions"][number];
type EditableSmsAction = Extract<
    EditableWorkflowAction,
    { __typename: "WorkflowActionSmsEntity" }
>;

export const SmsActionEditor: React.FC<SmsActionEditorProps> = props => {
    const { workflow, actionId, onChange, phoneLines, phoneLinesLoaded } = props;
    const editable =
        !!workflow.regionVisibility?.editable &&
        smsActionBlocker({
            loaded: phoneLinesLoaded,
            phoneLineCount: phoneLines.length,
        }) === null;

    const action = workflow.actions?.find(candidate => candidate.id === actionId);
    const sms = action?.__typename === "WorkflowActionSmsEntity" ? action : null;

    const commit = React.useCallback(
        (edit: (draft: EditableSmsAction) => void) => {
            const newWorkflow = produce(workflow, draft => {
                const draftAction = draft.actions?.find(
                    candidate => candidate.id === actionId,
                );
                if (draftAction?.__typename === "WorkflowActionSmsEntity") {
                    edit(draftAction);
                }
            });
            onChange(newWorkflow);
        },
        [workflow, actionId, onChange],
    );

    if (!sms) {
        return null;
    }

    const selectedLineMissing =
        !!sms.fromPhoneLineId &&
        phoneLinesLoaded &&
        !phoneLines.some(line => line.id === sms.fromPhoneLineId);

    return (
        <div className="space-y-4">
            <Select
                label=""
                value={sms.fromPhoneLineId ?? ""}
                disabled={!editable}
                onChange={e => {
                    const value = e.target.value;
                    commit(draft => {
                        draft.fromPhoneLineId = value
                            ? (value as TelecomPhoneLineId)
                            : null;
                    });
                }}>
                <option value="">Choose a phone number&hellip;</option>
                {phoneLines.map(line => (
                    <option key={line.id} value={line.id}>
                        {formatPhoneNumber(line.phoneNumber)}
                        {line.ownerName ? ` (${line.ownerName})` : ""}
                    </option>
                ))}
                {selectedLineMissing ? (
                    <option value={sms.fromPhoneLineId}>Currently selected number</option>
                ) : null}
            </Select>
            <TopAlignedCheckbox
                disabled={!editable}
                label="I confirm this workflow will only text people who have agreed to receive text messages from us, and I understand recipients can reply STOP to opt out at any time."
                checked={sms.consentAcknowledged}
                onChange={e => {
                    const checked = e.target.checked;
                    commit(draft => {
                        draft.consentAcknowledged = checked;
                    });
                }}
            />
        </div>
    );
};
